import type { MessageKey } from "./en";

/** Simplified Chinese UI catalog. Every key in {@link en} must be present. */
export const zhCN: Record<MessageKey, string> = {
  "app.title": "Rescript — 像编辑文字一样编辑视频",
  "common.cancel": "取消",
  "common.close": "关闭",
  "common.delete": "删除",
  "common.download": "下载",
  "common.import": "导入",
  "common.loading": "正在加载",
  "common.remove": "移除",
  "common.restore": "恢复",
  "common.retry": "重试",
  "common.searchOrCreate": "搜索或新建…",
  "common.settings": "设置",
  "common.system": "跟随系统",
  "common.tools": "工具",
  "settings.appearance": "外观",
  "settings.light": "浅色",
  "settings.dark": "深色",
  "settings.interfaceLanguage": "界面语言",
  "settings.privacy": "隐私",
  "settings.helpImprove": "帮助改进应用",
  "settings.telemetryHelp": "发送匿名功能使用统计和崩溃报告。",
  "settings.support": "支持与反馈",
  "settings.reportIssue": "报告问题",
  "settings.homepage": "主页",
  "settings.github": "GitHub",
  "settings.followX": "在 X 上关注",
  "model.transcriptSource": "转录来源",
  "model.language": "语言",
  "model.transcriptLanguage": "转录语言",
  "model.importTranscript": "导入转录文本",
  "upload.recentProjects": "最近项目",
  "upload.removeRecent": "从最近项目中移除",
  "upload.dropPrefix": "将视频或音频文件拖到此处，或者",
  "upload.browse": "浏览文件",
  "upload.chooseTranscriptFirst": "请先在上方菜单选择转录文本，再拖入媒体文件",
  "upload.willUseTranscript": "将使用 {name} · MP4、WebM、MOV、MP3、WAV 等",
  "upload.mediaFormats": "MP4、WebM、MOV、MP3、WAV、M4A 等",
  "upload.gettingReady": "正在准备",
  "upload.gettingReadyHelp": "正在设置媒体引擎，仅需一次。",
  "upload.unsupported": "此浏览器无法运行编辑器",
  "upload.unsupportedHelp":
    "编辑功能需要 SharedArrayBuffer，因此页面必须处于跨源隔离状态。请使用较新的 Chrome、Edge、Safari 或 Firefox，并通过 HTTPS 访问。",
  "upload.unsupportedSimd":
    "此设备无法运行编辑器",
  "upload.unsupportedSimdHelp":
    "编辑功能需要 WebAssembly SIMD，但此处理器未报告支持。在 Windows on Arm 电脑上，请更新到最新版 Rescript——当前安装程序包含无需模拟运行的原生 Arm64 版本。",
  "upload.transcribeTitle": "转录",
  "upload.transcribeText": "在本机运行 Whisper，或导入 SRT / VTT。",
  "upload.editTitle": "编辑",
  "upload.editText": "选择文字并按删除键即可剪除内容。",
  "upload.exportTitle": "导出",
  "upload.exportText": "将最终成片导出为 MP4 或 M4A。",
  "editor.chooseTranscript": "请先从转录来源菜单选择一个转录文本文件。",
  "editor.chooseMedia": "请选择视频或音频文件。",
  "editor.undo": "撤销 (⌘Z)",
  "editor.redo": "重做 (⇧⌘Z)",
  "editor.export": "导出",
  "topbar.startOver": "重新开始",
  "transcript.replace": "用 SRT、VTT 或 JSON 替换转录文本",
  "transcript.header": "转录文本",
  "transcript.wordDeleted": "已删除 {count} 个词",
  "transcript.wordsDeleted": "已删除 {count} 个词",
  "transcript.replaceConfirm": "要用此文件替换当前转录文本吗？",
  "transcript.invalidFile": "请选择 SRT、VTT 或 JSON 转录文本文件。",
  "transcript.noSpeech": "未检测到语音。请确认文件包含音轨，或导入转录文本。",
  "transcript.cut": "剪除",
  "transcript.follow": "跟随播放位置",
  "transcript.hideDeleted": "隐藏已删除文字",
  "transcript.showDeleted": "显示已删除文字",
  "transcript.correct": "纠正",
  "transcript.scrollWithPlayhead": "跟随播放位置滚动",
  "transcript.joinSplit": "片段分割点 — 点击以合并片段",
  "transcript.joinClips": "合并片段",
  "transcript.hesitation": "检测到未转录的停顿语气，可用“移除语气词”剪除",
  "transcript.textLabel": "转录文本",
  "transcript.textHint": "方向键在单词间移动并同步移动播放头。按住 Shift 加方向键进行选择。Backspace 或 Delete 剪除单词，Enter 更正，空格键播放或暂停，撤销可恢复剪除的内容。",
  "transcript.textHesitation": "［停顿］",
  "transcript.textCut": "已剪除：{text}",
  "transcript.textNothingToCut": "没有可剪除的内容",
  "transcript.textUndo": "撤销",
  "transcript.textRedo": "重做",
  "tools.bulk": "批量清理转录文本",
  "tools.removeFillers": "移除语气词",
  "tools.removeFillersTitle": "从视频中剪除“嗯”“呃”“…”等语气词",
  "tools.restoreFillers": "恢复语气词",
  "tools.restoreFillersTitle": "恢复所有已剪除的语气词",
  "tools.removeSilences": "移除静音",
  "tools.removeSilencesTitle": "剪除不少于 {seconds} 秒的停顿和静音",
  "tools.restoreSilences": "恢复静音",
  "tools.restoreSilencesTitle": "恢复所有已剪除的停顿",
  "timeline.removed": "已剪除 {trimmed} — 原始长度 {duration}",
  "timeline.back5": "后退 5 秒",
  "timeline.playPause": "播放 / 暂停（空格）",
  "timeline.forward5": "前进 5 秒",
  "timeline.split": "分割",
  "timeline.splitTitle": "在播放位置分割片段（S）",
  "timeline.splitDisabled": "请把播放位置移到保留区域后再分割",
  "timeline.delete": "删除",
  "timeline.deleteTitle": "删除所选片段（Delete）",
  "timeline.deleteDisabled": "请先在时间轴上选择要删除的片段",
  "timeline.restore": "恢复",
  "timeline.restoreTitle": "恢复所选剪除区域（Delete）",
  "timeline.restoreDisabled": "请先选择要恢复的剪除或静音区域",
  "timeline.zoomOut": "缩小",
  "timeline.fit": "适合窗口",
  "timeline.zoomIn": "放大 — 拖动文字边缘可微调时间",
  "timeline.joinClips": "合并片段（移除分割点）",
  "timeline.trimStart": "修剪片段开头",
  "timeline.trimEnd": "修剪片段结尾",
  "timeline.hesitationAdjust": "检测到停顿语气 — 可拖动边缘调整，或用“移除语气词”剪除",
  "timeline.hesitationCut": "检测到停顿语气 — 可用“移除语气词”剪除",
  "timeline.dragTiming": "{word} — 拖动边缘可调整时间",
  "timeline.scrollZoom": "滚动以缩放时间轴",
  "speaker.moveStart": "拖动以调整此说话人的起始位置",
  "speaker.moveLabel": "移动说话人标签",
  "speaker.options": "说话人选项",
  "speaker.change": "更改说话人",
  "speaker.rename": "重命名说话人",
  "speaker.renameAction": "重命名",
  "speaker.replace": "在整个项目中替换为…",
  "speaker.removeProject": "从项目中移除",
  "speaker.newName": "新名称",
  "speaker.find": "查找说话人…",
  "speaker.noMatches": "没有匹配的说话人",
  "speaker.renameTo": "将 {current} 重命名为 {next}",
  "speaker.button": "说话人",
  "speaker.search": "搜索或新建…",
  "speaker.defaultName": "说话人 {number}",
  "speaker.create": "新建“{name}”",
  "export.title": "导出",
  "export.type": "导出类型",
  "export.video": "视频",
  "export.audio": "音频",
  "export.transcript": "转录文本",
  "export.subtitles": "字幕",
  "export.videoUnavailable": "纯音频项目不能导出视频",
  "export.noAudio": "此文件没有音轨",
  "export.noWordsFirst": "请先转录或导入转录文本",
  "export.format": "格式",
  "export.resolution": "分辨率",
  "export.original": "原始",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "原始",
  "export.statCuts": "剪除",
  "export.statEdited": "成片",
  "export.includeTimestamps": "包含时间戳",
  "export.speakerLabels": "包含说话人姓名",
  "export.shortCues": "短字幕（≤5 秒）",
  "export.transcriptHelp": "带说话人标签的文本，已移除剪除内容。",
  "export.transcriptHelpTimestamps":
    "带说话人标签的文本，已移除剪除内容。每段以成片时间轴上的时间开头。",
  "export.transcriptHelpPdf":
    "PDF 使用标准拉丁字体 — 完整 Unicode（如中日韩）请优先选择 DOCX。",
  "export.subtitlesHelp":
    "SRT 和 VTT 使用已应用剪除的成片时间轴；JSON 保留完整词表以便重新导入。",
  "export.subtitlesHelpShortCues":
    "SRT 和 VTT 使用已应用剪除的成片时间轴，并拆成短句字幕（≤5 秒）。JSON 保留完整词表以便重新导入。",
  "export.encodingHelp": "使用 ffmpeg.wasm 重新编码，较长文件需要更多时间。",
  "export.reexport": "按最新编辑重新导出",
  "export.rendering": "正在浏览器中渲染…",
  "export.downloadFile": "下载 {name}",
  "export.saveFile": "保存 {name}…",
  "export.saving": "正在保存…",
  "export.saveDialogTitle": "保存导出文件",
  "export.savedAs": "已保存为 {name}",
  "export.showInFinder": "在访达中显示",
  "export.showInExplorer": "在资源管理器中显示",
  "export.showInFolder": "在文件夹中显示",
  "export.renderingNative": "正在渲染…",
  "export.encodingHelpNative": "使用 ffmpeg 重新编码，较长文件需要更多时间。",
  "export.downloadFormat": "下载 .{format}",
  "export.exportFormat": "导出 {format}",
  "export.timeline": "时间轴",
  "export.timelineTitle": "导出 NLE / DAW 序列（Resolve、Premiere、Final Cut、Pro Tools、Reaper、Blender）",
  "export.nle": "格式",
  "export.frameRate": "帧率",
  "export.timelineHelp": "序列按文件名引用原始媒体 — 导入后请在 NLE 中重新链接。",
  "export.timelineHelpAaf": "Pro Tools / Logic AAF（仅元数据）。",
  "export.timelineHelpFcpx": "Final Cut Pro FCPXML。",
  "export.timelineHelpPremiere": "Adobe Premiere Pro XML（xmeml）。",
  "export.timelineHelpResolve": "DaVinci Resolve XML（xmeml）。",
  "export.timelineHelpReaper": "Reaper 工程（.rpp）。",
  "export.timelineHelpSamplitude":
    "Samplitude EDL（.edl）— 也可在 Reaper 中直接打开。",
  "export.timelineHelpBlender":
    "Blender 视频编辑工程（.blend，Blender 5.2 及以上）— 请保存在与原始媒体相同的文件夹中。",
  "export.aafOverCap":
    "此编辑有 {count} 个片段；AAF 最多支持 {max} 个。请改用 Resolve、Premiere、Final Cut、Reaper 或 Samplitude EDL。",
  "export.preparing": "正在准备…",
  "import.reading": "正在读取转录文本…",
  "import.importing": "正在导入…",
  "import.failed": "导入失败",
  "import.readingFile": "正在读取文件…",
  "import.chooseFileShort": "请选择文件…",
  "import.chooseFile": "选择 SRT、VTT 或 JSON 转录文本",
  "import.selected": "已选择 {name}",
  "banner.faster": "想要更快地转录和导出？",
  "banner.getDesktop": "获取 Rescript 桌面客户端",
  "banner.downloadFor": "下载 {platform} 版",
  "banner.dismiss": "关闭",
  "social.githubRepo": "GitHub 仓库",
  "social.discordServer": "Discord 服务器",
  "social.xProfile": "X 个人主页",
  "globalError.title": "Rescript — 出现错误",
  "globalError.heading": "出现错误",
  "globalError.body":
    "编辑器遇到了意外错误。已保存的项目仍在此设备上，重新加载后即可恢复。",
  "progress.loadingMedia": "正在加载媒体…",
  "progress.loadingMediaEngine": "正在加载媒体引擎…",
  "progress.extractingAudio": "正在提取音频…",
  "progress.preparingMedia": "正在准备媒体…",
  "progress.loadingSpeechModel": "正在加载语音模型…",
  "progress.loadingSpeechCache": "正在从缓存加载语音模型…",
  "progress.downloadingSpeech": "正在下载语音模型…",
  "progress.gpuFallback": "GPU 已中断，正在改用 CPU…",
  "progress.detectingSpeech": "正在检测语音…",
  "progress.transcribing": "正在转录…",
  "progress.loadingAlignCache": "正在从缓存加载对齐模型…",
  "progress.downloadingAlign": "正在下载对齐模型…",
  "progress.aligning": "正在对齐文字…",
  "progress.speakers": "正在识别说话人…",
  "error.selectModel": "请选择用于转录的语音模型。",
  "error.workerCrashed": "转录进程发生崩溃。",
  "error.mediaEngineNetwork":
    "媒体引擎加载失败，网络连接已中断。请检查网络后重试。",
  "error.mediaEngineStalled":
    "媒体引擎停止响应，已重新启动。请关闭其他应用和标签页以释放内存，然后重试。",
  "error.mediaEngineNotReady":
    "媒体引擎尚未就绪，请刷新页面后重试。",
  "error.simdUnsupported":
    "此处理器不支持媒体引擎所需的 WebAssembly SIMD。在 Windows on Arm 电脑上，请更新到最新版 Rescript——当前安装程序包含无需模拟运行的原生 Arm64 版本。",
  "error.processFile": "无法处理此文件。",
  "error.extractAudio": "无法从此文件提取音频。",
  "error.nothingToExport": "所有内容都已删除，没有可导出的内容。",
  "error.videoExport": "渲染视频时导出失败。",
  "error.audioExport": "渲染音频时导出失败。",
  "error.exportSave": "无法保存导出的文件。",
  "error.diskFull": "磁盘空间不足，无法完成。请释放一些空间后重试。",
  "error.export": "导出失败。",
  "error.timelineExport": "时间轴导出失败。",
  "error.timelineEmpty": "时间轴上没有可放入的内容。",
  "error.aafTemplate": "无法加载 AAF 导出模板。",
  "error.emptyTranscript": "转录文本文件为空。",
  "error.noTimedWords": "转录文本中没有带时间信息的文字。",
  "error.parseJson": "无法解析 JSON 转录文本。",
  "error.jsonShape": 'JSON 必须是文字数组，或包含 words 字段的对象。',
  "error.noWords": "没有可导出的文字。",
  "error.projectMissing": "此项目已不在已保存项目中。",
  "error.openProject": "无法打开此项目。",
  "error.removeProject": "无法移除此项目。",
  "error.readTranscript": "无法读取此转录文本。",
  "error.clearRecent": "无法清除最近项目。",
  "error.modelDownload":
    "语音模型下载未完成，网络连接已中断。请检查网络后重试；已下载完成的部分会被保留。",
  "error.gpuReset":
    "GPU 重置导致转录中断（常见于锁屏后），请重试。",
  "confirm.clearRecent": "要移除所有最近项目吗？已保存的编辑也会被删除。",
};
